/**
 * Easy bot tier — a thin, transparent heuristic ported from the reference
 * `mahjong-teacher.jsx` `usefulness`/`pickDiscard` logic. It scores every tile
 * by how much it contributes to a would-be hand (pairs/triplets, same-suit
 * connectivity, middle-rank value) and discards the least useful; it claims
 * greedily (any Ron, then any Pung, then any Chi). No lookahead, no belief
 * tracking — just enough to make a bot that plays legal, roughly sensible
 * mahjong so a learner has opponents. Pure and seed-deterministic.
 */

import { RANKS, countByKey, tileKey, type Seat, type Tile } from '../game/types.js';
import {
  addedKongAvailable,
  canExposedKong,
  concealedKongOptions,
  type Claim,
  type SeatClaims,
} from '../game/Claims.js';
import type { MahjongState } from '../game/GameState.js';
import type { Rng } from '../utils/prng.js';
import type { AiStrategy, KongDecision } from './types.js';

/** Own-kind count that already forms (or all but completes) a triplet. */
const TRIPLET_COUNT = 3;
const PAIR_COUNT = 2;
const TRIPLET_BONUS = 12;
const PAIR_BONUS = 7;

/** Same-suit neighbour rewards: an immediate neighbour (±1) is worth more
 *  than a one-gap neighbour (±2), since it completes a run more ways. */
const ADJACENT_OFFSETS: readonly number[] = [-2, -1, 1, 2];
const ADJACENT_STEP = 1;
const ADJACENT_BONUS = 3.5;
const GAP_BONUS = 1.2;

const MIDDLE_RANK_LOW = 4;
const MIDDLE_RANK_HIGH = 6;
const MIDDLE_BONUS = 1;
const TERMINAL_LOW: number = RANKS[0] ?? 1;
const TERMINAL_HIGH: number = RANKS[RANKS.length - 1] ?? 9;
const TERMINAL_PENALTY = 0.5;

/** A lone honour (a single wind/dragon) can never grow into a run — dead weight. */
const LONE_HONOR_PENALTY = 1;

/** Seeded tie-break jitter added to each discard score so equal tiles vary. */
const DISCARD_NOISE = 0.4;

/**
 * How much `tile` contributes to `hand`'s winning potential — higher is more
 * worth keeping. Ported faithfully from the reference heuristic, adapted to
 * this repo's `Tile` shape via `countByKey`/`tileKey`.
 */
export function usefulness(tile: Tile, hand: readonly Tile[]): number {
  const counts = countByKey(hand);
  const own = counts.get(tileKey(tile)) ?? 0;
  let score = 0;
  if (own >= TRIPLET_COUNT) score += TRIPLET_BONUS;
  else if (own === PAIR_COUNT) score += PAIR_BONUS;

  if (tile.kind === 'suit') {
    for (const offset of ADJACENT_OFFSETS) {
      const rank = tile.rank + offset;
      if (rank < TERMINAL_LOW || rank > TERMINAL_HIGH) continue;
      const neighbourKey = `${tile.suit}${rank}`;
      if ((counts.get(neighbourKey) ?? 0) > 0) {
        score += Math.abs(offset) === ADJACENT_STEP ? ADJACENT_BONUS : GAP_BONUS;
      }
    }
    if (tile.rank >= MIDDLE_RANK_LOW && tile.rank <= MIDDLE_RANK_HIGH) score += MIDDLE_BONUS;
    else if (tile.rank === TERMINAL_LOW || tile.rank === TERMINAL_HIGH) score -= TERMINAL_PENALTY;
  } else if (own === 1) {
    score -= LONE_HONOR_PENALTY;
  }
  return score;
}

class EasyStrategy implements AiStrategy {
  chooseDiscard(hand: readonly Tile[], rng: Rng): Tile {
    const first = hand[0];
    if (first === undefined) throw new Error('cannot choose a discard from an empty hand');
    let worst = first;
    let worstScore = Number.POSITIVE_INFINITY;
    for (const tile of hand) {
      const score = usefulness(tile, hand) + rng() * DISCARD_NOISE;
      if (score < worstScore) {
        worstScore = score;
        worst = tile;
      }
    }
    return worst;
  }

  chooseClaim(_seat: Seat, _state: MahjongState, claims: SeatClaims): Claim | null {
    if (claims.canRon) return { type: 'ron', seat: claims.seat };
    if (claims.canPung) return { type: 'pung', seat: claims.seat };
    const chi = claims.chi[0];
    if (chi !== undefined) return { type: 'chi', seat: claims.seat, option: chi };
    return null;
  }

  chooseKong(seat: Seat, state: MahjongState): KongDecision | null {
    if (state.phase === 'awaiting-discard' && seat === state.turn) {
      const concealed = concealedKongOptions(state.hands[seat] ?? [], state.ruleset)[0];
      if (concealed !== undefined) return { kind: 'concealed', tile: concealed };
      if (
        state.drawnTile !== null &&
        addedKongAvailable(state.melds[seat] ?? [], state.drawnTile, state.ruleset)
      ) {
        return { kind: 'added', tileId: state.drawnTile.id };
      }
      return null;
    }
    if (
      state.phase === 'claim-window' &&
      state.lastDiscard !== null &&
      seat !== state.lastDiscard.from &&
      canExposedKong(state.hands[seat] ?? [], state.lastDiscard.tile, state.ruleset)
    ) {
      return { kind: 'exposed' };
    }
    return null;
  }

  chooseTsumo(_hand: readonly Tile[], _meldCount: number): boolean {
    return true;
  }
}

/** The single Easy-tier instance — stateless, so one shared instance is safe. */
export const EASY_STRATEGY: AiStrategy = new EasyStrategy();
